import { PASSWORD_MIN_LENGTH } from '../utils/passwordUtils';

/**
 * Maps a backend error code to a localized, user-facing message.
 *
 * Each branch returns a translation looked up from a plain string literal (never
 * from a variable), so the i18n Jira-integrity guard stays satisfied. The
 * error-code constants themselves remain stable identifiers. An unknown code
 * falls back to a generic translated message.
 *
 * @param t - the translation function from useTranslation().
 * @param code - the backend error code.
 * @returns the localized message.
 */
const getErrorMessage = (t, code) => {
  switch (code) {
    // R1-D's permission-gate 403 (requirePermission/requireAnyPermission) — names no mission.
    case 'PERMISSION_DENIED':
      return t('Action reserved for operators. Check that you are not signed in as a technician in another tab.');
    // Request operations
    case 'REQUEST_CREATION_FAILED':
      return t('An error occurred while creating the request');
    case 'QUOTA_EXCEEDED':
      return t('Quota reached: this request cannot be created.');
    case 'QUOTA_WARNING':
      return t('Quota exceeded (warning): you can create this request, but you have already reached your quota.');
    case 'INTERVENTION_TRANSITION_UNAVAILABLE':
      return t('This ticket cannot be validated from its current status. Refresh the page, or contact support if it persists.');
    // Password reset
    case 'RESET_EMAIL_SENT':
      return t('If an account exists with this identifier, an email has been sent.');
    case 'RESET_RATE_LIMITED':
      return t('Too many attempts. Please try again later.');
    case 'RESET_TOKEN_REQUIRED':
      return t('Token and password are required.');
    case 'RESET_PASSWORD_WEAK':
    case 'PASSWORD_WEAK':
      return t(
        'The password must contain at least {{min}} characters, including an uppercase letter, a lowercase letter, a digit and a special character.',
        { min: PASSWORD_MIN_LENGTH }
      );
    case 'PASSWORD_TOO_LONG':
      return t('The password must not exceed 72 bytes');
    case 'RESET_TOKEN_INVALID':
      return t('Invalid or expired link. Please request a new one.');
    case 'RESET_USER_NOT_FOUND':
      return t('User not found.');
    case 'RESET_PASSWORD_SUCCESS':
      return t('Your password has been reset successfully.');
    case 'INVITATION_LINK_STILL_VALID':
      return t('The latest invitation link is still valid.');
    case 'INVITATION_USER_INACTIVE':
      return t('This account is inactive: no invitation can be sent.');
    // Referentials
    case 'REFERENTIAL_CODE_TAKEN':
      return t('This value already exists in the referential.');
    case 'REFERENTIAL_NOT_FOUND':
      return t('This value no longer exists.');
    case 'REFERENTIAL_NOT_MIGRATED':
      return t('The referentials are not available on this environment: the database migration has not been run.');
    case 'REFERENTIAL_FETCH_FAILED':
      return t('Unable to load the referential.');
    case 'REFERENTIAL_MANAGE_FAILED':
      return t('Unable to save this referential value.');
    // Boundary validation. The zod `details` are English paths written for a log, so the bubble
    // stays generic and the client's own rules are what point at the offending fields.
    case 'VALIDATION_FAILED':
      return t('Some fields are invalid — check the form.');
    case 'TECHNICIAN_USERNAME_TAKEN':
      return t('This sign-in identifier is already used by another account: change the first or last name.');
    // The email's cause sits on another screen (Users): the sentence names it and the next gesture.
    case 'TECHNICIAN_EMAIL_TAKEN_INACTIVE':
      return t('A deactivated account already uses this email. Open it in Users, reactivate it, set its role to Technician, then fill in its record.');
    case 'TECHNICIAN_EMAIL_TAKEN':
      return t('This email is already used by an active account with another role. Look it up in Users.');
    case 'TECHNICIAN_EMAIL_TAKEN_BY_TECHNICIAN':
      return t('This email already belongs to a technician, active or deactivated. Open their record from the Technicians directory.');
    case 'EMAIL_TAKEN_BY_TECHNICIAN':
      return t('This email already belongs to a technician: find it in Technicians.');
    case 'TECHNICIAN_UNKNOWN_REFERENTIAL':
      return t('A zone, skill or country picked here no longer exists. Reload the page.');
    case 'TECHNICIAN_FORBIDDEN':
      return t('You are not allowed to manage technicians.');
    case 'TECHNICIAN_NOT_FOUND':
      return t('This technician no longer exists. Go back to the directory.');
    case 'EMAIL_TAKEN_INACTIVE':
      return t('A deactivated account already uses this email: find it in the list (status « Inactive ») and reactivate it.');
    case 'EMAIL_TAKEN':
      return t('A user with this email already exists.');
    // Unavailabilities. The three refusals a technician can act on: move the start, adjust the
    // dates, or stop trying to remove a period that is already over.
    case 'UNAVAILABILITY_STARTS_IN_PAST':
      return t('A period cannot start before today.');
    case 'UNAVAILABILITY_OVERLAP':
      return t('This period overlaps one you have already declared. Delete that one first, or adjust these dates.');
    case 'UNAVAILABILITY_FINISHED':
      return t('This period is over and can no longer be deleted.');
    // The assignment cycle. Each one names the next gesture, because there is no expiry to fall
    // back on (D5): the person who clicked has to know whether to reload, pick somebody else,
    // fix an account, or correct a postal code.
    // ⚠️ Since L5b the two 409s of the send are **different facts** (D45): several technicians may
    // be solicited in parallel, so « already live » no longer means "somebody is being asked" —
    // it means somebody **accepted**, and the mission is closed. « Already solicited » is the one
    // that says this technician, and only him, is already on it.
    case 'INTERVENTION_REQUEST_ALREADY_LIVE':
      return t('A technician has already accepted this mission, so it takes no further solicitation. Reload it.');
    case 'INTERVENTION_REQUEST_ALREADY_SOLICITED':
      return t('This technician has already been solicited on this mission. Reload it: somebody may have sent it just before you.');
    case 'INTERVENTION_REQUEST_REPLACED_MOVED':
      return t('The request you were replacing has moved — the technician answered. Reload the mission.');
    case 'INTERVENTION_REQUEST_NOT_LIVE':
      return t('This request is no longer live: it has been answered or replaced. Reload the mission.');
    case 'INTERVENTION_REQUEST_INVALID_TECHNICIAN':
      return t('This account is not an active technician.');
    case 'INTERVENTION_REQUEST_OUTSIDE_PERIMETER':
      return t('This technician does not cover the mission department.');
    case 'INTERVENTION_REQUEST_UNKNOWN_AREA':
      return t('No department matches this postal code — check it, or add the prefix to the referential.');
    // The technician's two 409s, and they are **never** collapsed into one sentence (R-050).
    // `respond()` already tells the three causes apart, and the most frequent one is a mission the
    // back office reassigned to somebody else: telling that technician "you have already answered"
    // would claim he did something he never did.
    case 'INTERVENTION_REQUEST_ALREADY_ANSWERED':
      return t('You have already answered this request.');
    case 'INTERVENTION_REQUEST_MISSION_TAKEN':
      return t('This mission has been given to somebody else.');
    // The two gates that sit in front of the routes above. They used to send no code at all, so
    // the front had nothing to translate and rendered the backend's own English — « Issue not
    // found on this service desk (HTTP 404) », in a French drawer.
    case 'MISSION_NOT_ON_DESK':
      return t('This mission does not belong to the selected service desk. Switch desk, then reopen it.');
    case 'INTERVENTION_REQUEST_NOT_FOUND':
      return t('This request no longer exists.');
    case 'UNKNOWN_SPECIALTY':
    case 'UNKNOWN_EXPERTISE':
      return t('One of these skills no longer exists.');
    // The desk's country, enforced server-side (D68-c): the picker only offers it, so this is a
    // screen left open while the referential moved, or a call made around it.
    case 'FOREIGN_SPECIALTY':
    case 'FOREIGN_EXPERTISE':
      return t('One of these skills belongs to another country than this service desk. Reload the panel.');
    // The qualification workflow (D16/D17). The first four are refusals — a 4xx that sent
    // nothing. The last two are the opposite: the write went through and only the Jira leg
    // failed, so they name the transition somebody now has to do by hand.
    case 'INTERVENTION_REQUEST_MISSION_NOT_QUALIFYING':
      return t('This mission is no longer being qualified — somebody has already scheduled it. Reload the tab.');
    case 'INTERVENTION_REQUEST_MISSING_INTERVENTION_DATE':
      return t('This mission has no intervention date. Set one, save it, then send.');
    case 'INTERVENTION_QUALIFICATION_DISABLED':
      return t('Writing to Jira is not enabled for this service desk: the date cannot be saved from here.');
    case 'INTERVENTION_DATE_LOCKED':
      return t('The intervention date can only be changed while the mission is being qualified. Reload the tab: this one has already been scheduled.');
    case 'INTERVENTION_QUALIFICATION_JIRA_FAILED':
      return t('The technician has been solicited, but the ticket did not move to « Intervention Programmée » in Jira. Make the transition by hand on the ticket — the solicitation itself went out and needs nothing.');
    // L6 — the deadline, the technician's slot and the reminders (E12, E14, E15).
    case 'INTERVENTION_REQUEST_AFTER_DEADLINE':
      return t('This day is after the deadline. Pick an earlier day.');
    case 'INTERVENTION_REQUEST_CHOSEN_SLOT_PAST':
      return t('This day or time has already passed. Pick a later slot.');
    case 'INTERVENTION_REQUEST_DEADLINE_LOCKED':
      return t('Locked: a technician has accepted.');
    case 'INTERVENTION_REQUEST_NOT_PENDING':
      return t('This request is no longer pending: the technician has just answered. Reload the mission.');
    case 'INTERVENTION_REQUEST_REMINDED_RECENTLY':
      return t('This technician was reminded too recently.');
    case 'INTERVENTION_REQUEST_REMINDER_LIMIT':
      return t('3 reminders without an answer — solicit another technician.');
    case 'INTERVENTION_REQUEST_DEADLINE_PASSED':
      return t('Deadline passed — change the deadline or cancel.');
    case 'INTERVENTION_CHOSEN_SLOT_JIRA_FAILED':
      return t('The acceptance is recorded, but the chosen day was not written on the Jira ticket. Write it on the ticket by hand.');
    case 'INTERVENTION_QUALIFICATION_REVERSE_FAILED':
      return t('The technician has been removed, but the ticket stayed on « Intervention Programmée » in Jira. Move it back to « En cours de qualification » by hand, otherwise the mission will not come back into this tab.');
    default:
      return t('An error occurred');
  }
};

export { getErrorMessage };
